import React, { ReactElement } from "react";
import { useModal } from "@hooks/useModalContext";
import styles from "./footer.module.scss";
import { useDomainContext } from "@hooks/useDomainContext";
import { convertToBreakpointVariables } from "@page-builder/utils";
import { injectDoNotSellInFooter } from "@lib/sharedUtils";
import { replacePlaceholders } from "@lib/sharedUtils";

const yearNow = new Date();

interface Link {
    title: string;
    href?: string;
    onClick?: () => void;
}

export default function Footer({
    textColor,
    bgColor,
    addCcpaPrivacyNotice,
}: {
    textColor?: string;
    bgColor?: string;
    addCcpaPrivacyNotice?: boolean;
}): ReactElement {
    const { setModal } = useModal();

    const { domain, pageType, locale, defaultLocale, domainNameTranslation } =
        useDomainContext();
    const { visitDetails, routeConfig, category } = useDomainContext();
    const privacyPolicyLabel =
        category?.config?.privacyPolicyLabel ??
        category?.parentCategory?.config?.privacyPolicyLabel;
    const disclaimerNote = domain?.config?.disclaimerNote ?? null;
    let contactUsLink = null;
    if (
        visitDetails?.trafficSourceNetwork?.toLowerCase() === "outbrain" ||
        category?.slug === "home-security" ||
        domain?.name === "bestcontractors.com"
    ) {
        contactUsLink = (
            <a className="cursor-pointer" href="/contact-us">
                Contact Us
            </a>
        );
    }

    let footerLinks: Link[] = [
        {
            title:
                domain?.translations?.[
                    `${domainNameTranslation}_TermsConditions`
                ] ?? "Terms & Conditions",
            onClick: () => setModal({ active: true, content: "terms" }),
        },
        {
            title:
                privacyPolicyLabel ||
                domain?.translations?.[
                    `${domainNameTranslation}_PrivacyPolicy`
                ] ||
                "Privacy Policy",
            onClick: () => setModal({ active: true, content: "privacy" }),
        },
        // only show partner-with-us when its not on the partner-with-us page
        ...(pageType !== "partner-with-us"
            ? [
                  {
                      title:
                          domain?.translations?.[
                              `${domainNameTranslation}_PartnerWithUs`
                          ] ?? "Partner with Us",
                      href:
                          locale === (defaultLocale ?? "en")
                              ? "/partner-with-us"
                              : `/${locale}/partner-with-us`,
                  },
              ]
            : []),
    ];

    if (domain?.config?.isBranded) {
        footerLinks = footerLinks.filter(
            (link) => link.title !== "Partner with Us",
        );
    }

    const ccpaTextBlock = domain.textBlocks?.find(
        (textBlock) =>
            textBlock.position === "custom-domain-ccpa-privacy-notice" ||
            textBlock.position === "custom-category-ccpa-privacy-notice",
    );
    if (addCcpaPrivacyNotice || ccpaTextBlock) {
        footerLinks.push({
            title: "CCPA Privacy Notice",
            onClick: () =>
                setModal({
                    active: true,
                    content: "ccpaPrivacyNotice",
                }),
        });
    }

    injectDoNotSellInFooter({
        footerArray: footerLinks,
        pageType: pageType,
        trafficSource: visitDetails?.trafficSource,
        hideDoNotSell: domain.config?.hideDoNotSell,
        domain: domain,
        visitDetails: visitDetails,
        domainNameTranslation: domainNameTranslation,
        locale: locale,
        defaultLocale: defaultLocale as string,
        routeConfig: routeConfig,
    });

    const variables = routeConfig?.config?.footer?.variables;

    return (
        <footer
            style={{
                color: textColor,
                backgroundColor: bgColor,
                ...(variables ? convertToBreakpointVariables(variables) : {}),
            }}
            className={`${styles["footer"]}  text-center lg:px-6 lg:py-0`}
        >
            <div className={`${styles["wrapper"]} max-w-7xl mx-auto`}>
                <div
                    className={`${
                        styles["inner"]
                    } lg:flex lg:justify-between lg:items-center flex-row-reverse ${
                        !bgColor ? "border-t" : ""
                    } lg:mx-auto px-4 lg:px-0 py-6 pb-36 lg:py-4 ltrStyle`}
                >
                    <div className={`footer-menu ${styles["footer-menu"]}`}>
                        <ul className="flex justify-center flex-col md:flex-row mb-3 lg:mb-0  children:last:border-r-0 ltrStyle">
                            {footerLinks.map((item, index) => (
                                <li
                                    key={index}
                                    className={`px-2 pb-2 md:pb-0 font-medium lg:font-light text-sm lg:leading-4 ${styles["menu-item"]} `}
                                >
                                    <a
                                        className=" cursor-pointer"
                                        onClick={item.onClick}
                                        href={item.href}
                                    >
                                        <span>{item.title}</span>
                                    </a>
                                </li>
                            ))}
                            {contactUsLink && (
                                <li
                                    className={`px-2 font-medium lg:font-light text-sm lg:leading-4 ${styles["menu-item"]} `}
                                >
                                    <span>{contactUsLink}</span>
                                </li>
                            )}
                        </ul>
                    </div>
                    <div className="font-light text-sm">
                        {disclaimerNote ? (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: replacePlaceholders(disclaimerNote),
                                }}
                            />
                        ) : (
                            <span>
                                <span>©</span>{" "}
                                <span>{yearNow.getFullYear()}</span>{" "}
                                <a href={`https://${domain.name}`}>
                                    {domain.niceName}
                                </a>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}
